import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis
} from 'recharts';
import { apiClient } from '../../apiClient';
import { API_ENDPOINTS, ROUTES } from '../../constants';
import { Product } from '../../types';
import { extractApiErrorMessage } from '../../utils/errorHandler';
import { formatCurrency } from '../../utils/pricing';
import { resolveImageUrl } from '../../utils/image';

interface Analytics {
  daily: Array<{ _id: string; revenue: number; orders: number }>;
  last30Days: { revenue: number; orders: number };
  allTime: { revenue: number; orders: number };
  averageOrderValue: number;
  topProducts: Array<{ _id: string; name?: string; image?: string; quantity: number; revenue: number }>;
  statusCounts: Record<string, number>;
  paymentPending: number;
  pendingReviews?: number;
  bySource?: Array<{
    source: string;
    visits: number;
    visitsToday: number;
    orders: number;
    revenue: number;
  }>;
  byCampaign?: Array<{
    campaign: string;
    visits: number;
    visitsToday: number;
    orders: number;
    revenue: number;
  }>;
}

const StatCard: React.FC<{ label: string; value: string; hint?: string; to?: string }> = ({
  label,
  value,
  hint,
  to
}) => (
  <Paper
    variant="outlined"
    component={to ? RouterLink : 'div'}
    {...(to ? { to } : {})}
    sx={{
      p: 2.5,
      flex: 1,
      minWidth: 170,
      textDecoration: 'none',
      color: 'inherit',
      transition: 'border-color 150ms',
      ...(to ? { '&:hover': { borderColor: 'primary.main' } } : {})
    }}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h5" sx={{ fontWeight: 700, my: 0.5 }}>
      {value}
    </Typography>
    {hint && (
      <Typography variant="caption" color="text.secondary">
        {hint}
      </Typography>
    )}
  </Paper>
);

const AdminDashboardPage: React.FC = () => {
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [lowStock, setLowStock] = useState<Product[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [lowStockThreshold, setLowStockThreshold] = useState(5);

  useEffect(() => {
    apiClient
      .get(`${API_ENDPOINTS.ORDERS}/analytics/summary`)
      .then((res) => setAnalytics(res.data))
      .catch((err) => setError(extractApiErrorMessage(err, 'Failed to load analytics')));

    Promise.all([
      apiClient.get(`${API_ENDPOINTS.PRODUCTS}?limit=100`),
      apiClient.get(`${API_ENDPOINTS.SITE_SETTINGS}/store`).catch(() => null)
    ])
      .then(([productsRes, storeRes]) => {
        const threshold = storeRes?.data?.store?.lowStockThreshold ?? 5;
        setLowStockThreshold(threshold);
        const products: Product[] = productsRes.data?.products || [];
        setLowStock(products.filter((p) => (p.stock ?? 0) <= threshold).slice(0, 6));
      })
      .catch(() => {});
  }, []);

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!analytics) {
    return (
      <Box>
        <Skeleton width={220} height={40} />
        <Box sx={{ display: 'flex', gap: 2, my: 2 }}>
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} variant="rounded" height={100} sx={{ flex: 1 }} />
          ))}
        </Box>
        <Skeleton variant="rounded" height={280} />
      </Box>
    );
  }

  const chartData = analytics.daily.map((d) => ({
    date: d._id.slice(5),
    revenue: d.revenue,
    orders: d.orders
  }));

  const pendingOrders = analytics.statusCounts['pending'] || 0;

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
        Dashboard
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Last 30 days at a glance
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <StatCard
          label="Revenue (30d)"
          value={formatCurrency(analytics.last30Days.revenue)}
          hint={`All-time: ${formatCurrency(analytics.allTime.revenue)}`}
        />
        <StatCard
          label="Orders (30d)"
          value={String(analytics.last30Days.orders)}
          hint={`All-time: ${analytics.allTime.orders}`}
        />
        <StatCard label="Avg order value" value={formatCurrency(analytics.averageOrderValue)} />
        <StatCard
          label="Needs attention"
          value={String(pendingOrders + analytics.paymentPending + (analytics.pendingReviews || 0))}
          hint={`${pendingOrders} pending orders · ${analytics.paymentPending} unpaid${
            analytics.pendingReviews ? ` · ${analytics.pendingReviews} reviews to moderate` : ''
          }`}
          to={`${ROUTES.ADMIN}/orders`}
        />
      </Box>

      <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Revenue — last 30 days
        </Typography>
        {chartData.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
            No orders in the last 30 days yet.
          </Typography>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#D65A88" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#D65A88" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} width={70} />
              <ChartTooltip
                formatter={(value: any, name: any) =>
                  name === 'revenue' ? [formatCurrency(Number(value)), 'Revenue'] : [value, 'Orders']
                }
              />
              <Area type="monotone" dataKey="revenue" stroke="#D65A88" strokeWidth={2} fill="url(#rev)" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
          Traffic &amp; sales by source (30d)
        </Typography>
        {(analytics.bySource?.length ?? 0) === 0 ? (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            No tagged visits yet. Scans of QR links like vediluxe.com/?src=event will show up
            here within a few minutes.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Source</TableCell>
                <TableCell align="right">Visits today</TableCell>
                <TableCell align="right">Visits (30d)</TableCell>
                <TableCell align="right">Orders</TableCell>
                <TableCell align="right">Revenue</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {analytics.bySource!.map((s) => (
                <TableRow key={s.source} hover>
                  <TableCell>
                    <Chip size="small" label={s.source} />
                  </TableCell>
                  <TableCell align="right">{s.visitsToday}</TableCell>
                  <TableCell align="right">{s.visits}</TableCell>
                  <TableCell align="right">{s.orders}</TableCell>
                  <TableCell align="right">{formatCurrency(s.revenue)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {(analytics.byCampaign?.length ?? 0) > 0 && (
        <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
            Campaigns (30d)
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Campaign</TableCell>
                <TableCell align="right">Visits today</TableCell>
                <TableCell align="right">Visits (30d)</TableCell>
                <TableCell align="right">Orders</TableCell>
                <TableCell align="right">Revenue</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {analytics.byCampaign!.map((c) => (
                <TableRow key={c.campaign} hover>
                  <TableCell>
                    <Chip size="small" variant="outlined" label={c.campaign} />
                  </TableCell>
                  <TableCell align="right">{c.visitsToday}</TableCell>
                  <TableCell align="right">{c.visits}</TableCell>
                  <TableCell align="right">{c.orders}</TableCell>
                  <TableCell align="right">{formatCurrency(c.revenue)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr' }, gap: 3 }}>
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
            Top products (30d)
          </Typography>
          {analytics.topProducts.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              No sales yet.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell align="right">Sold</TableCell>
                  <TableCell align="right">Revenue</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {analytics.topProducts.map((p) => (
                  <TableRow key={p._id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {p.image && (
                          <Box
                            component="img"
                            src={resolveImageUrl(p.image)}
                            alt=""
                            sx={{ width: 34, height: 34, borderRadius: 1, objectFit: 'cover' }}
                          />
                        )}
                        <Typography variant="body2">{p.name || 'Deleted product'}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">{p.quantity}</TableCell>
                    <TableCell align="right">{formatCurrency(p.revenue)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
            Low stock (≤ {lowStockThreshold} left)
          </Typography>
          {lowStock.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              All products well stocked. ✨
            </Typography>
          ) : (
            lowStock.map((p) => (
              <Box
                key={p._id}
                component={RouterLink}
                to={`${ROUTES.ADMIN}/products/${p._id}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  py: 1,
                  textDecoration: 'none',
                  color: 'inherit',
                  borderBottom: '1px solid rgba(0,0,0,0.05)',
                  '&:last-child': { borderBottom: 0 }
                }}
              >
                <Box
                  component="img"
                  src={resolveImageUrl(p.images?.[0] || '')}
                  alt=""
                  sx={{ width: 34, height: 34, borderRadius: 1, objectFit: 'cover' }}
                />
                <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                  {p.name}
                </Typography>
                <Chip
                  size="small"
                  color={p.stock === 0 ? 'error' : 'warning'}
                  label={p.stock === 0 ? 'Out of stock' : `${p.stock} left`}
                />
              </Box>
            ))
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default AdminDashboardPage;
